import { t } from '@lingui/core/macro'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { type ChatApi, createChatApi } from 'features/chatbot/api/chatApi'
import { reconnectToStream } from 'features/chatbot/api/sseClient'
import type { ChatMessage } from 'features/chatbot/types/chat'
import type { ChatStreamEvent } from 'features/chatbot/types/stream'
import { useCallback, useMemo, useRef } from 'react'

const POLL_INTERVAL_MS = 2000
const MAX_POLL_ATTEMPTS = 30

interface UseReconnectParams {
  sessionId: string | null
  streamId: string | null
  lastSeq: number
  onEvent: (event: ChatStreamEvent) => void
  onMessagesLoaded: (messages: ChatMessage[]) => void
  onError: (error: string) => void
  onComplete: () => void
  /** Reconnected stream was severed again before a terminal event. */
  onDisconnect?: () => void
}

interface UseReconnectReturn {
  reconnect: () => void
  stopPolling: () => void
}

async function loadPersistedConversation(
  api: ChatApi,
  sessionId: string,
  onMessagesLoaded: (messages: ChatMessage[]) => void,
  onComplete: () => void,
  onError: (error: string) => void,
  abortRef: { current: boolean }
): Promise<void> {
  let attempts = 0

  const tick = async () => {
    if (abortRef.current) return

    try {
      const result = await api.loadMessages({ sessionId })
      onMessagesLoaded(result.messages)

      const lastAssistant = [...result.messages]
        .reverse()
        .find((m) => m.role === 'assistant')

      if (!lastAssistant || lastAssistant.status === 'complete') {
        onComplete()
        return
      }

      if (lastAssistant.status === 'error') {
        onError(lastAssistant.content || t`Assistant message errored`)
        onComplete()
        return
      }

      attempts += 1
      if (attempts >= MAX_POLL_ATTEMPTS) {
        onComplete()
        return
      }
      if (!abortRef.current) {
        setTimeout(tick, POLL_INTERVAL_MS)
      }
    } catch (error) {
      if (!abortRef.current) {
        onError(
          error instanceof Error ? error.message : t`Load messages failed`
        )
      }
    }
  }

  await tick()
}

export function useReconnect({
  sessionId,
  streamId,
  lastSeq,
  onEvent,
  onMessagesLoaded,
  onError,
  onComplete,
  onDisconnect,
}: UseReconnectParams): UseReconnectReturn {
  const { authorization } = useGeckoAuth()
  const { LLM_SERVICE } = useRuntimeVariables()

  const pollingAbortRef = useRef({ current: false })

  const api = useMemo(
    () => createChatApi(LLM_SERVICE, authorization ?? ''),
    [LLM_SERVICE, authorization]
  )

  const stopPolling = useCallback(() => {
    pollingAbortRef.current.current = true
  }, [])

  const reconnect = useCallback(() => {
    if (!sessionId) return

    pollingAbortRef.current.current = true
    pollingAbortRef.current = { current: false }

    if (streamId) {
      const connection = reconnectToStream(api, streamId, lastSeq, sessionId, {
        onEvent,
        onError,
        onComplete,
        onDisconnect,
      })

      connection.gonePromise.then((isGone) => {
        if (isGone && sessionId) {
          loadPersistedConversation(
            api,
            sessionId,
            onMessagesLoaded,
            onComplete,
            onError,
            pollingAbortRef.current
          )
        }
      })

      return
    }

    loadPersistedConversation(
      api,
      sessionId,
      onMessagesLoaded,
      onComplete,
      onError,
      pollingAbortRef.current
    )
  }, [
    sessionId,
    streamId,
    lastSeq,
    api,
    onEvent,
    onMessagesLoaded,
    onError,
    onComplete,
    onDisconnect,
  ])

  return { reconnect, stopPolling }
}
